/**
 * Centralized Video Management Module
 * Handles all video autoplay/pause logic across sliders with a single IntersectionObserver
 * 
 * Performance optimizations:
 * - Single shared IntersectionObserver for all videos
 * - Lazy initialization only for visible sliders
 * - Automatic cleanup when videos are removed from DOM
 */

class VideoManager {
  constructor() {
    this.observer = null;
    this.videos = new WeakMap(); // Track video states without memory leaks
    this.initialized = false;
  }

  init() {
    if (this.initialized) return;
    
    // Create a single IntersectionObserver for all videos
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          const video = entry.target;
          
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            // Video is 50%+ visible - play it
            this.playVideo(video);
          } else if (!entry.isIntersecting || entry.intersectionRatio < 0.5) {
            // Video is not visible or less than 50% visible - pause it
            this.pauseVideo(video);
          }
        });
      },
      {
        root: null,
        rootMargin: '50px', // Start loading slightly before visible
        threshold: [0, 0.5, 1.0]
      }
    );
    
    // Initialize mute toggle functionality
    this.initMuteToggles();
    
    this.initialized = true;
  }

  /**
   * Initialize mute toggle functionality for all video mute buttons
   */
  initMuteToggles() {
    // Use event delegation to handle dynamically added videos
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-video-mute-toggle]')) {
        e.preventDefault();
        e.stopPropagation();
        
        const button = e.target.closest('[data-video-mute-toggle]');
        const video = button.parentElement.querySelector('video');
        
        if (video) {
          this.toggleMute(video, button);
        }
      }
    });
  }

  /**
   * Toggle mute state of a video and update the button icon
   * @param {HTMLVideoElement} video - The video element
   * @param {HTMLElement} button - The mute toggle button
   */
  toggleMute(video, button) {
    const muteIcon = button.querySelector('.mute-icon');
    const unmuteIcon = button.querySelector('.unmute-icon');
    
    if (video.muted) {
      // Unmute the video
      video.muted = false;
      video.volume = 0.5; // Set a reasonable volume level
      
      // Update icons
      muteIcon.classList.add('hidden');
      unmuteIcon.classList.remove('hidden');
      
      // Update aria-label
      button.setAttribute('aria-label', 'Mute video');
    } else {
      // Mute the video
      video.muted = true;
      
      // Update icons
      muteIcon.classList.remove('hidden');
      unmuteIcon.classList.add('hidden');
      
      // Update aria-label
      button.setAttribute('aria-label', 'Unmute video');
    }
  }

  /**
   * Register a video for automatic play/pause management
   * @param {HTMLVideoElement} video - The video element to manage
   */
  observeVideo(video) {
    if (!video || !(video instanceof HTMLVideoElement)) {
      return;
    }
    
    // Initialize observer if not already done
    if (!this.initialized) {
      this.init();
    }
    
    // Skip if already observing this video
    if (this.videos.has(video)) {
      return;
    }
    
    // Track this video
    this.videos.set(video, {
      isPlaying: false,
      hasPlayed: false
    });
    
    // Set up video attributes for better performance
    video.muted = true; // Required for autoplay
    video.loop = true; // Ensure videos loop continuously
    video.playsInline = true; // Better mobile support
    video.preload = 'metadata'; // Don't load full video until needed
    
    // Ensure video source is loaded (for cloned videos in sliders)
    if (video.dataset.src && (!video.src || video.src === '')) {
      // Check if source element exists and needs updating
      let sourceEl = video.querySelector('source');
      if (!sourceEl) {
        sourceEl = document.createElement('source');
        video.appendChild(sourceEl);
      }
      sourceEl.src = video.dataset.src;
      sourceEl.type = video.dataset.type || 'video/mp4';
      video.load(); // Force reload with new source
    }
    
    // Prevent context menu on videos
    if (!video.dataset.contextMenuDisabled) {
      video.addEventListener('contextmenu', (e) => {
        e.preventDefault();
      });
      
      video.dataset.contextMenuDisabled = 'true';
    }
    
    // Start observing
    this.observer.observe(video);
    
    // Force immediate autoplay attempt
    setTimeout(() => {
      this.playVideo(video);
    }, 100);
  }

  /**
   * Stop observing a video (cleanup)
   * @param {HTMLVideoElement} video
   */
  unobserveVideo(video) {
    if (!video || !this.observer) return;
    
    this.observer.unobserve(video);
    this.videos.delete(video);
    
    // Pause and cleanup
    video.pause();
    video.currentTime = 0;
  }

  /**
   * Play a video with error handling - AGGRESSIVE autoplay
   * @param {HTMLVideoElement} video
   */
  playVideo(video) {
    if (!video) return;
    
    const state = this.videos.get(video);
    if (state && state.isPlaying) return; // Already playing
    
    // Force autoplay - don't wait for readyState
    video.play().then(() => {
      if (state) {
        state.isPlaying = true;
        state.hasPlayed = true;
      }
      // console.log('✅ Video started playing:', video.src || video.dataset.src);
    }).catch((error) => {
      console.log('❌ Video autoplay failed:', error.message);
      // Try again after a short delay
      setTimeout(() => {
        video.play().catch(() => {
          console.log('❌ Video autoplay retry failed');
        });
      }, 500);
    });
  }

  /**
   * Pause a video
   * @param {HTMLVideoElement} video
   */
  pauseVideo(video) {
    if (!video) return;
    
    const state = this.videos.get(video);
    if (state) {
      state.isPlaying = false;
    }
    
    video.pause();
  }

  /**
   * Batch observe multiple videos (more efficient than one-by-one)
   * @param {NodeList|Array} videos - Collection of video elements
   */
  observeVideos(videos) {
    videos.forEach(video => this.observeVideo(video));
  }

  /**
   * Find and observe all videos in a container
   * @param {HTMLElement} container - Container to search for videos
   */
  observeVideosInContainer(container) {
    if (!container) return;
    
    const videos = container.querySelectorAll('video[data-video-slider]');
    this.observeVideos(videos);
  }

  /**
   * Cleanup - stop observing all videos
   */
  destroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    this.videos = new WeakMap();
    this.initialized = false;
  }
}

// Create singleton instance
window.videoManager = window.videoManager || new VideoManager();

// Auto-initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    // Find all existing videos and observe them
    const allVideos = document.querySelectorAll('video[data-video-slider]');
    window.videoManager.observeVideos(allVideos);
  });
} else {
  // DOM already loaded
  const allVideos = document.querySelectorAll('video[data-video-slider]');
  window.videoManager.observeVideos(allVideos);
}

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = VideoManager;
}

